import { CSSProperties } from 'react';
import useAppVersions from 'hooks/useAppVersions';
import { formatVersionLines } from 'utils/version';

type VersionLabelProps = {
  /** Applied to the row, so the label inherits its host panel's treatment. */
  style?: CSSProperties;
};

/**
 * Renders the app's versions as one row of separately labelled parts:
 *
 *   Hardware 1.2.3   Client 1.4.0
 *        ↑            ↑
 *   the native   the JS it is
 *   shell the    actually running
 *   player
 *   installed
 *
 * Deliberately never a single bare "Version: x.y.z". The two numbers diverge
 * as soon as an OTA update lands, and a support ticket or crash report that
 * names only one of them is ambiguous about which code the player was on.
 *
 * One row rather than a stacked pair: this is a footnote under Log Out, and two
 * stacked lines there read as content. They stay two separate elements with
 * their own labels — the row is a layout choice, not a merge — so "Hardware"
 * and "Client" are still individually readable and selectable.
 *
 * On web there is no shell and no OTA, so it renders a single "Version x.y.z"
 * instead; on native it shows the Client part first and adds the Hardware part
 * once the async shell read resolves.
 */
const VersionLabel = ({ style }: VersionLabelProps) => {
  const versions = useAppVersions();
  return (
    <div style={{ ...rowStyle, ...style }}>
      {formatVersionLines(versions).map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  );
};

export default VersionLabel;

// flexWrap so a long pair still fits a narrow panel instead of forcing the
// row wider than its host. Hosts set justifyContent (the centred footers) or
// take the default start alignment (the left-aligned panel headers).
const rowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  columnGap: '0.5rem'
};
